// src/widgets/widgets/UpcomingBills.tsx
import { useMemo } from 'react';
import { Paper, Text, Stack, Group, Badge } from '@mantine/core';
import { IconCalendarDue } from '@tabler/icons-react';
import dayjs from 'dayjs';
import type { WidgetProps } from '../types';

function UpcomingBills({ bills }: WidgetProps) {
	const upcomingBills = useMemo(() => {
		const now = dayjs();
		const nextWeek = now.add(7, 'day');

		return bills
			.filter((bill) => {
				if (bill.paid || !bill.dueDate) return false;
				const dueDate = dayjs(bill.dueDate);
				return dueDate.isAfter(now) && dueDate.isBefore(nextWeek);
			})
			.sort((a, b) => dayjs(a.dueDate).diff(dayjs(b.dueDate)))
			.slice(0, 5);
	}, [bills]);

	const formatDueDate = (date: string | null) => {
		if (!date) return '';
		const due = dayjs(date);
		const today = dayjs();
		const diffDays = due.diff(today, 'day');

		if (diffDays === 0) return 'Today';
		if (diffDays === 1) return 'Tomorrow';
		return due.format('ddd, MMM D');
	};

	return (
		<Paper withBorder p='md' radius='md'>
			<Group gap='xs' mb='md'>
				<IconCalendarDue size={18} />
				<Text fw={600}>Upcoming Bills</Text>
			</Group>

			{upcomingBills.length === 0 ? (
				<Text size='sm' c='dimmed' ta='center' py='md'>
					No bills due in the next 7 days
				</Text>
			) : (
				<Stack gap='xs'>
					{upcomingBills.map((bill) => (
						<Group key={bill.id} justify='space-between'>
							<Stack gap={0}>
								<Text size='sm' fw={500}>
									{bill.name}
								</Text>
								<Text size='xs' c='dimmed'>
									{formatDueDate(bill.dueDate)}
								</Text>
							</Stack>
							<Badge variant='light' color='blue'>
								${bill.amount.toFixed(2)}
							</Badge>
						</Group>
					))}
				</Stack>
			)}
		</Paper>
	);
}

export default UpcomingBills;
